import { useMemo, useState } from 'react';

import { Grid } from '@mui/material';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useQuery } from '@redwoodjs/web';

import Loader from 'src/components/Loader/Loader';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast } from 'src/components/Toasts/toast';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { MERCHANT_TABLE_HEADERS } from 'src/constants';
import { GET_MERCHANTS } from 'src/graphql/queries/merchants.query';
import { useEventListener } from 'src/hooks/useEventListener';
import { disableCopyEventHandler } from 'src/hooks/useEventListener/eventHandlers';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import {
    filteredMerchantsTableData,
    FilterOptionsType,
    updateTableFilters,
} from 'src/utils/tableUtils';

const ViewMerchantsPage = () => {
    const [tableFilters, setTableFilters] = useState({});

    const { data, loading } = useQuery(GET_MERCHANTS, {
        fetchPolicy: 'cache-and-network',
        onError: () => {
            return showErrorToast('There was an error retrieving the merchants');
        },
    });

    const filterOptions = () => {
        const merchants = data?.merchants?.data ?? [];
        return Object.fromEntries([
            [
                'Trading Name',
                Array.from(
                    new Set(
                        merchants
                            .map(({ tradingName }) => {
                                return tradingName;
                            })
                            .filter(Boolean)
                    )
                ).map((tradingName) => {
                    return {
                        value: tradingName,
                        label: tradingName,
                    };
                }),
            ],
            [
                'Merchant Account Number',
                Array.from(
                    new Set(
                        merchants
                            .map(({ merchantAccountNumber }) => {
                                return merchantAccountNumber;
                            })
                            .filter(Boolean)
                    )
                ).map((merchantAccountNumber) => {
                    return {
                        value: merchantAccountNumber,
                        label: merchantAccountNumber,
                    };
                }),
            ],

            [
                'Partner Name',
                Array.from(
                    new Set(
                        merchants.map(({ partnerName }) => {
                            return partnerName;
                        })
                    )
                )
                    .filter((partnerName) => {
                        return partnerName;
                    })
                    .map((partnerName) => {
                        return {
                            value: partnerName,
                            label: partnerName,
                        };
                    }),
            ],
            [
                'Top Level Partner',
                Array.from(
                    new Set(
                        merchants.map(({ topLevelPartner }) => {
                            return topLevelPartner;
                        })
                    )
                )
                    .filter((topLevelPartner) => {
                        return topLevelPartner;
                    })
                    .map((topLevelPartner) => {
                        return {
                            value: topLevelPartner,
                            label: topLevelPartner,
                        };
                    }),
            ],
            [
                'MID (Merchant ID)',
                Array.from(
                    new Set(
                        merchants.map(({ mid }) => {
                            return mid;
                        })
                    )
                )
                    .filter((mid) => {
                        return mid;
                    })
                    .map((mid) => {
                        return {
                            value: mid,
                            label: mid,
                        };
                    }),
            ],
            [
                'Application ID',
                Array.from(
                    new Set(
                        merchants
                            .map(({ applicationId }) => {
                                return applicationId;
                            })
                            .filter(Boolean)
                    )
                ).map((applicationId) => {
                    return {
                        value: applicationId,
                        label: String(applicationId),
                    };
                }),
            ],
        ]) as FilterOptionsType;
    };

    // Handle add merchant
    const handleAddRecord = () => {
        navigate('/merchant/add');
    };

    const tableData = useMemo(() => {
        return filteredMerchantsTableData(data?.merchants?.data, tableFilters).map((merchant) => {
            return {
                ...merchant,
                status: <StatusChip status={merchant?.status} type="merchant" />,
            };
        });
    }, [data, tableFilters]);

    const handleViewMerchant = ({ merchantAccountNumber }) => {
        navigate(`/merchant/${merchantAccountNumber}`);
    };

    // Do not allow disgruntled employees to easily and quickly copy
    useEventListener('copy', disableCopyEventHandler);

    return (
        <PageContent
            heading="Merchants"
            subHeading="Add, View, Remove and Update NPI Merchants as necessary"
        >
            <MetaTags title="Merchants" description="Merchants Page" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    {/* Display loader if data is still fetching */}
                    {loading ? (
                        <Loader />
                    ) : (
                        <TableList
                            addRecordButtonText="Add Merchant"
                            data={tableData}
                            headers={MERCHANT_TABLE_HEADERS}
                            title="Merchants List"
                            addRecordCallBack={handleAddRecord}
                            handleViewRecord={handleViewMerchant}
                            // TODO: add the delete functionality
                            deleteRecordCallBack={() => {}}
                            enableFilter
                            enableExport
                            disableContentCopy
                            disableContentCut
                            filterOptions={filterOptions()}
                            updateTableFilters={(filter: string, newFilterValue: string) => {
                                return setTableFilters((prevFilters) => {
                                    return updateTableFilters(prevFilters, newFilterValue, filter);
                                });
                            }}
                        />
                    )}
                </Grid>
            </Grid>
        </PageContent>
    );
};

export default ViewMerchantsPage;
